'use client';

import { useState } from 'react';
import { Heart, MessageCircle, Share2, Bookmark, MoreHorizontal, Flag, Star } from 'lucide-react';
import type { Post } from './FeedCenter';

interface PostCardProps {
  post: Post;
  onLike: (id: string) => void;
  onBookmark: (id: string) => void;
}

export default function PostCard({ post, onLike, onBookmark }: PostCardProps) {
  const [showMenu, setShowMenu] = useState(false);
  const [shareCount, setShareCount] = useState(post.shares);
  const [shared, setShared] = useState(false);

  const handleShare = () => {
    if (!shared) {
      setShareCount((c) => c + 1);
      setShared(true);
    }
  };

  return (
    <article className="card-elevated card-hover p-5 group">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3">
          {/* Avatar */}
          <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${post.authorGradient} flex items-center justify-center text-white text-sm font-700 flex-shrink-0`}>
            {post.authorInitials}
          </div>

          {/* Author info */}
          <div>
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-600 text-foreground hover:text-accent cursor-pointer transition-colors">
                {post.authorName}
              </span>
              {post.verified && (
                <Star size={12} className="text-accent fill-accent" />
              )}
            </div>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span>{post.authorHandle}</span>
              <span>·</span>
              <span>{post.timeAgo}</span>
            </div>
          </div>
        </div>

        {/* Right header: intent badge + menu */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-primary/10 border border-primary/15">
            <div className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
            <span className="text-xs font-600 text-accent numeric-tabular">{post.intentScore}%</span>
          </div>

          {/* Menu */}
          <div className="relative">
            <button
              onClick={() => setShowMenu((v) => !v)}
              className="w-7 h-7 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-all opacity-0 group-hover:opacity-100 duration-150"
              aria-label="Post options"
            >
              <MoreHorizontal size={15} />
            </button>
            {showMenu && (
              <div className="absolute right-0 top-8 z-10 w-44 card-elevated border border-border rounded-xl shadow-xl overflow-hidden animate-fade-in">
                {[
                  { label: 'Not interested', action: () => setShowMenu(false) },
                  { label: 'Hide from feed', action: () => setShowMenu(false) },
                  { label: 'Report post', icon: Flag, action: () => setShowMenu(false), danger: true },
                ].map((item) => (
                  <button
                    key={`menu-${item.label}`}
                    onClick={item.action}
                    className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left transition-colors ${
                      item.danger
                        ? 'text-red-400 hover:bg-red-500/10' :'text-foreground hover:bg-muted'
                    }`}
                  >
                    {item.icon && <item.icon size={13} />}
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="mb-3">
        <p className="text-sm text-foreground/90 leading-relaxed whitespace-pre-wrap">
          {post.content}
        </p>
      </div>

      {/* Media */}
      {post.mediaType === 'code' && post.mediaContent && (
        <div className="mb-3 rounded-xl bg-background border border-border overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2 border-b border-border bg-muted/30">
            <div className="flex gap-1">
              <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
              <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
              <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
            </div>
            <span className="text-xs text-muted-foreground font-mono">Cypher Query</span>
          </div>
          <pre className="px-4 py-3 text-xs text-cyan-300 font-mono overflow-x-auto leading-relaxed">
            {post.mediaContent}
          </pre>
        </div>
      )}

      {post.mediaType === 'quote' && post.mediaContent && (
        <div className="mb-3 px-4 py-3 rounded-xl border-l-2 border-primary/60 bg-primary/5">
          <p className="text-sm text-muted-foreground italic leading-relaxed">{post.mediaContent}</p>
        </div>
      )}

      {/* Topics */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        {post.topics.map((topic) => (
          <span key={`${post.id}-topic-${topic}`} className="topic-chip text-xs">
            #{topic}
          </span>
        ))}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-3 border-t border-border/50">
        <div className="flex items-center gap-1">
          {/* Like */}
          <button
            onClick={() => onLike(post.id)}
            className={`post-action-btn ${post.liked ? 'liked' : ''}`}
            aria-label={`${post.liked ? 'Unlike' : 'Like'} post`}
          >
            <Heart size={15} className={post.liked ? 'fill-current' : ''} />
            <span className="numeric-tabular">{post.likes}</span>
          </button>

          {/* Comment */}
          <button className="post-action-btn" aria-label="Comment on post">
            <MessageCircle size={15} />
            <span className="numeric-tabular">{post.comments}</span>
          </button>

          {/* Share */}
          <button
            onClick={handleShare}
            className={`post-action-btn ${shared ? 'text-secondary' : ''}`}
            aria-label="Share post"
          >
            <Share2 size={15} />
            <span className="numeric-tabular">{shareCount}</span>
          </button>
        </div>

        {/* Bookmark */}
        <button
          onClick={() => onBookmark(post.id)}
          className={`post-action-btn ${post.bookmarked ? 'text-amber-400' : ''}`}
          aria-label={`${post.bookmarked ? 'Remove bookmark' : 'Bookmark'} post`}
        >
          <Bookmark size={15} className={post.bookmarked ? 'fill-current' : ''} />
        </button>
      </div>
    </article>
  );
}